import { useState, useEffect, useCallback } from 'react'
import { useSetRecoilState } from 'recoil'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import { orderFormAutoFillQueueState } from '@/recoil/OrderForm'
import usePreviousValue from '@/hooks/usePreviousValue'
import numberFormat from '@/utils/numberFormat'
import { ORDER_INPUT_MAP } from '@/constants/order'

export default function OrderBookQuote({
  side,
  price,
  size,
  total,
  maxQuoteSize,
  priceFormat,
  sizeFormat,
  isAsk
}) {
  const prevSize = usePreviousValue(size)
  const setOrderFormAutoFillQueue = useSetRecoilState(
    orderFormAutoFillQueueState
  )
  const [isSizeChangeUp, setSizeChangeUp] = useState(false)
  const [isSizeChangeDown, setSizeChangeDown] = useState(false)
  const [resetStatusTimer, setResetStatusTimer] = useState(false)

  const triggerOrderFormAutoFill = useCallback(
    ({ price, size }) => {
      const orderFormAutoFillQueue = []
      price &&
        orderFormAutoFillQueue.push({
          key: ORDER_INPUT_MAP.price,
          value: Number(price)
        })
      size &&
        orderFormAutoFillQueue.push({
          key: ORDER_INPUT_MAP.size,
          value: Number(size)
        })
      orderFormAutoFillQueue.length > 0 &&
        setOrderFormAutoFillQueue(orderFormAutoFillQueue)
    },
    [setOrderFormAutoFillQueue]
  )

  useEffect(() => {
    const clearTimer = () => clearTimeout(resetStatusTimer)
    if (size === prevSize) {
      return clearTimer
    }
    const resetStatus = () => {
      setSizeChangeUp(false)
      setSizeChangeDown(false)
    }
    clearTimer()
    resetStatus()
    setResetStatusTimer(setTimeout(resetStatus, 300))
    if (size > prevSize) {
      setSizeChangeUp(true)
    } else if (size < prevSize) {
      setSizeChangeDown(true)
    }
    return clearTimer
  }, [prevSize, size, resetStatusTimer])

  const barWidthPercentage = BigNumber(total).div(maxQuoteSize).times(-100)
  const barChartStyle = {
    transform: `translate3d(${barWidthPercentage}%, 0, 0)`
  }

  return (
    <div className={`order-book-quote ${side}-quote`}>
      <div
        className={clsx('order-book-cell price-cell', {
          'color-positive': !isAsk,
          'color-negative': isAsk
        })}
        onClick={() => triggerOrderFormAutoFill({ price })}
      >
        {numberFormat(price, priceFormat)}
      </div>
      <div className="order-book-cell-wrap">
        <div className={`bar bar-chart-${side}`} style={barChartStyle} />
        <div className="data-group-wrap">
          <div
            className={clsx('order-book-cell size-cell', {
              'is-up': isSizeChangeUp,
              'is-down': isSizeChangeDown
            })}
            onClick={() => triggerOrderFormAutoFill({ size })}
          >
            {numberFormat(size, sizeFormat)}
          </div>
          <div
            className="order-book-cell total-cell"
            onClick={() => triggerOrderFormAutoFill({ price, size: total })}
          >
            {numberFormat(total, sizeFormat)}
          </div>
        </div>
      </div>
    </div>
  )
}
